import { useMemo } from 'react';

import { useGetIntakeQuery } from '@/services/api/intakeService';
import { useGetStaffNotesQuery } from '@/services/api/staffNotesService';

import {
  extractNoteContent,
  INCIDENT_SUMMARY_HEADER,
} from '../../case-ingestion/utils/notesParser';

export type HumanSummarySource = 'ingestion' | 'intake';

interface UseHumanIncidentSummaryResult {
  humanSummary: string | null;
  source: HumanSummarySource | null;
  isLoading: boolean;
}

/**
 * Checks both human-entered sources for an incident summary in priority order:
 * 1. Case ingestion staff note (header: "From Case Ingestion - Incident Summary")
 * 2. Intake description (form_data.incident_info.description)
 *
 * Returns the first non-empty human summary found, or null if neither exists.
 */
export function useHumanIncidentSummary({
  matterId,
  intakeId,
}: {
  matterId: string;
  intakeId?: string | null;
}): UseHumanIncidentSummaryResult {
  const { data: staffNotes, isLoading: isLoadingNotes } =
    useGetStaffNotesQuery({ matterId }, { skip: !matterId });

  const { data: intake, isLoading: isLoadingIntake } = useGetIntakeQuery(
    intakeId || '',
    { skip: !intakeId },
  );

  const result = useMemo((): Omit<UseHumanIncidentSummaryResult, 'isLoading'> => {
    // Priority 1: Case ingestion staff note
    const ingestionNote = staffNotes?.find((n) =>
      n.content.startsWith(INCIDENT_SUMMARY_HEADER),
    );
    if (ingestionNote) {
      const content = extractNoteContent(
        ingestionNote.content,
        INCIDENT_SUMMARY_HEADER,
      );
      if (content) {
        return { humanSummary: content, source: 'ingestion' };
      }
    }

    // Priority 2: Intake description
    const intakeDescription = intake?.form_data?.incident_info?.description;
    if (intakeDescription?.trim()) {
      return { humanSummary: intakeDescription.trim(), source: 'intake' };
    }

    return { humanSummary: null, source: null };
  }, [staffNotes, intake]);

  return {
    ...result,
    isLoading: isLoadingNotes || (!!intakeId && isLoadingIntake),
  };
}
